import Link from 'components/Globals/Base/Link';
import { DrawerToggleButton } from 'components/Header/HeaderStyles';
import MyAccount from 'components/MyAccount';
import Placeholder from 'components/Placeholder';
import { sourceSubscribeCTA } from 'constants/constants';
import { Bars } from 'icons/Bars';
import { Logo } from 'icons/Logo';
import type CustomWindow from 'interfaces/Utils';
import { UserContext } from 'providers/UserProvider';
import { type ForwardedRef, forwardRef, useContext, useRef } from 'react';
import { trackEvent } from 'services/Gtm/functions';
import { useLongPress } from '@/app_lib/hooks/useLongPress';
import { FortuneReleaseModal, type FortuneReleaseModalRef } from '../FortuneReleaseModal';
import * as S from './MobileNavigationBarStyles';
import { FortuneReleaseBadge } from '@/app_components/release-tools/FortuneReleaseBadge';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';
import { shouldShowSubscribeButton } from '@/main/lib/utils/navigationUtils';

declare let window: CustomWindow;

export interface MobileNavigationBarProps {
  domainLogo: string | null;
  subDomain: string;
  logoHref: string;
}

// biome-ignore lint/security/noDangerouslySetInnerHtml: our own html
const renderDomainLogo = (html?: string | null) => html && <div dangerouslySetInnerHTML={{ __html: html }} />;

const MobileNavigationBar = (props: MobileNavigationBarProps, ref: ForwardedRef<HTMLLabelElement> | null) => {
  const { optimizely } = useOptimizely();

  const { domainLogo, subDomain, logoHref } = props;
  const { userState: user, pianoScriptFailed } = useContext(UserContext);
  const releaseModalRef = useRef<FortuneReleaseModalRef>(null);

  const longPressHandlers = useLongPress({
    duration: 500,
    onLongPress: () => {
      releaseModalRef.current?.openModal();
    },
  });

  const displayLogo = ['crypto', 'well'].includes(subDomain);
  const redirect = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';

  const burgerTrackingData = {
    contentPlacementCD: 'Global Side navigation',
    eventAction: 'Global Side navigation toggle',
    eventCategory: 'navigation',
    eventLabel: 'open',
  };

  const trackingData = {
    contentPlacementCD: 'header',
    eventAction: 'top header links',
    eventCategory: 'navigation',
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  return (
    <S.NavigationMobileContent key='mobile'>
      <S.LeftSide>
        <DrawerToggleButton
          htmlFor='drawer-toggle'
          className='hamburger-toggle'
          onClick={() => {
            if (window.s2nApi) {
              window.s2nApi.float = false;
            }
            if (trackEvent && burgerTrackingData) trackEvent(burgerTrackingData);
          }}
          onKeyDown={handleKeyDown}
          aria-label='Open menu'
          data-cy='burger-button'
          ref={ref}
          tabIndex={0}
          {...longPressHandlers}
        >
          <Bars
            onClick={() => trackOptimizelyEvent(optimizely, 'navigation_hamburger_menu_click', {})}
            isMobile
          />
        </DrawerToggleButton>
        <Link
          href={logoHref}
          ariaLabel='Homepage'
          dataCy='logo'
          className='logo'
        >
          {(displayLogo && renderDomainLogo(domainLogo)) || <Logo />}
          <FortuneReleaseBadge />
        </Link>
      </S.LeftSide>
      <S.RightSide>
        {user.isLoggedIn === null && user.hasAccess === null && pianoScriptFailed === false && (
          <Placeholder
            size={{ height: 40, width: 100 }}
            className='account-placeholder'
          />
        )}

        {shouldShowSubscribeButton(user.isLoggedIn, user.hasAccess, user.isPaused) && (
          <Link
            href={`/subscribe/?itm_source=${sourceSubscribeCTA}&redirect=${encodeURIComponent(redirect)}`}
            trackingData={{
              ...trackingData,
              eventLabel: 'subscribe now',
            }}
            dataCy='subscribeBtnMobile'
            type='accentButton'
            size={{ default: 'xsmall' }}
            hasRightArrow={false}
            className='subscribe-cta-mobile'
          >
            Subscribe for $1
          </Link>
        )}

        {user.isLoggedIn && <MyAccount />}
      </S.RightSide>
      <FortuneReleaseModal ref={releaseModalRef} />
    </S.NavigationMobileContent>
  );
};

export default forwardRef(MobileNavigationBar);
